import React from "react";

import { Button, Grid } from "react-bootstrap";
import { CALL_STATUSES } from "../callBatchDetails/statusConfig";
import UploadBatchModal from "../uploadBatchModal/UploadBatchModal";
import "../callWorkspace.css";

const statusClass = (status) =>
  "cw-status cw-status-" + status.toLowerCase().replace(/\s+/g, "-");

const BatchDetails = ({
  batch,
  leads,
  stats,
  assignees,
  assignee,
  status,
  onAssigneeChange,
  onStatusChange,
  onClearFilters,
  onBack,
  onOpenLead,
  loading,
  error,
  pagination,
  onRetry,
  onPageChange,
  assigneesLoading,
  assigneesError,
  onRetryAssignees,
  canManageAssignments,
  assignmentLead,
  selectedAssigneeId,
  assignmentSaving,
  assignmentError,
  onAssignLead,
  onSelectAssignee,
  onSaveLeadAssignment,
  onCloseAssignment,
}) => {
  const canAssignLead = (lead) =>
    !["interested", "closed", "blacklisted"].includes(lead.outcome);

  return (
  <main className="cw-page">
    <Grid fluid>
      <button type="button" className="cw-back-link" onClick={onBack}>
        ← Back to Batch Management
      </button>
      <div className="cw-header">
        <div>
          <p className="cw-kicker">BATCH MANAGEMENT</p>
          <h1>{batch ? batch.name : loading ? "Loading batch…" : "Batch not found"}</h1>
          <p>Review assigned leads and their current call status.</p>
        </div>
      </div>
      <section className="cw-panel cw-batch-stats-panel">
        <div className="cw-section-heading">
          <div>
            <h2>Batch overview</h2>
            <p>
              {assignee === "all"
                ? "All assigned leads"
                : "Status breakdown for " + ((assignees.find((item) => String(item.id) === String(assignee)) || {}).name || "selected caller")}
            </p>
          </div>
        </div>
        <div className="cw-batch-stat-grid">
          {stats.map((item) => (
            <div className="cw-batch-stat" key={item.label}>
              <span>{item.label}</span>
              <strong>
                {item.count}
                <small>/{item.total}</small>
              </strong>
            </div>
          ))}
        </div>
      </section>
      <section className="cw-panel cw-batch-leads-panel">
        <div className="cw-batch-filter-bar" aria-label="Filter batch leads">
          <label>
            Assignee
            <select
              value={assignee}
              onChange={(event) => onAssigneeChange(event.target.value)}
            >
              <option value="all">All assignees</option>
              {assignees.map((user) => (
                <option key={user.id} value={user.id}>
                  {user.name}
                </option>
              ))}
            </select>
          </label>
          <label>
            Status
            <select
              value={status}
              onChange={(event) => onStatusChange(event.target.value)}
            >
              <option value="all">All statuses</option>
              {CALL_STATUSES.map((item) => (
                <option key={item.value} value={item.value}>
                  {item.label}
                </option>
              ))}
            </select>
          </label>
          <Button type="button" onClick={onClearFilters}>
            Clear filters
          </Button>
        </div>
        {assigneesError ? (
          <div className="cw-tab-error" role="alert">
            Could not load callers: {assigneesError}{" "}
            <Button type="button" bsStyle="link" onClick={onRetryAssignees}>
              Retry
            </Button>
          </div>
        ) : null}
        {error ? <div className="cw-tab-error" role="alert">{error} <Button bsStyle="link" onClick={onRetry}>Retry</Button></div> : null}
        <div className="cw-section-heading">
          <div>
            <h2>Assigned leads</h2>
            <p>{pagination.total} leads shown</p>
          </div>
        </div>
        <div className="cw-table-wrap" tabIndex="0" aria-label="Assigned leads">
          <table className="cw-table">
            <thead>
              <tr>
                <th>Lead name</th>
                <th>Number</th>
                <th>Assigned to</th>
                <th>Status</th>
                <th>Status reason</th>
                <th>Attempts</th>
                <th>Last updated</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan="8" className="cw-table-state">Loading leads…</td></tr>
              ) : leads.length ? (
                leads.map((lead) => (
                  <tr key={lead.id}>
                    <td>
                      <strong>{lead.name}</strong>
                    </td>
                    <td>{lead.phone}</td>
                    <td>{lead.assignee || "Unassigned"}</td>
                    <td>
                      <span className={statusClass(lead.status)}>
                        {lead.status}
                      </span>
                    </td>
                    <td>{lead.statusReason || "—"}</td>
                    <td>{lead.attempts}</td>
                    <td>{lead.lastUpdated}</td>
                    <td>
                      {canManageAssignments ? (
                        <Button
                          type="button"
                          bsStyle="primary"
                          className="cw-row-assign-button"
                          disabled={assigneesLoading || !!assigneesError || !canAssignLead(lead)}
                          title={!canAssignLead(lead) ? "Completed or Don't Call leads can’t be reassigned." : undefined}
                          onClick={() => onAssignLead(lead)}
                        >
                          {lead.assigneeId ? "Reassign" : "Assign"}
                        </Button>
                      ) : null}{" "}
                      <Button
                        type="button"
                        bsStyle="link"
                        onClick={() => onOpenLead(lead)}
                      >
                        View
                      </Button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="8" className="cw-table-state">
                    <strong>No leads match these filters</strong>
                    <span>Try another assignee or status.</span>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        <div className="cw-pagination" aria-label="Batch lead pages">
          <span>{pagination.total} leads</span>
          <div>
            <Button type="button" disabled={pagination.current_page <= 1 || loading} onClick={() => onPageChange(pagination.current_page - 1)}>Previous</Button>
            <span>Page {pagination.current_page} of {pagination.last_page}</span>
            <Button type="button" disabled={pagination.current_page >= pagination.last_page || loading} onClick={() => onPageChange(pagination.current_page + 1)}>Next</Button>
          </div>
        </div>
      </section>
    </Grid>
    <UploadBatchModal
      show={!!assignmentLead}
      onHide={onCloseAssignment}
      singleLead
      leadName={assignmentLead ? assignmentLead.name : ""}
      batchName={batch ? batch.name : ""}
      users={assignees}
      usersLoading={assigneesLoading}
      usersError={assigneesError}
      onRetryUsers={onRetryAssignees}
      selectedUserId={selectedAssigneeId}
      onSelectUser={onSelectAssignee}
      assigning={assignmentSaving}
      assignmentError={assignmentError}
      onUpload={onSaveLeadAssignment}
      currentAssigneeId={assignmentLead ? assignmentLead.assigneeId : null}
      currentAssigneeName={assignmentLead ? assignmentLead.assignee : ""}
    />
  </main>
  );
};

export default BatchDetails;
